import type {
  FramingCropPathPoint,
  FramingSceneInspector,
} from "../framingInspector";

/**
 * Scene-relative timeline lanes for the workbench (CROP-WB-010). Observed
 * keyframes and interpolated spans stay visually distinct, gaps stay gaps,
 * and truncated history never draws an apparently complete track.
 */

export type FramingTimelineLaneKind =
  | "planned_path"
  | "applied_path"
  | "subject_track"
  | "qc_events"
  | "feedback";

export type FramingTimelinePoint = {
  timeSeconds: number;
  kind: "observed_keyframe" | "event" | "feedback";
  label: string;
  anomaly: boolean;
};

export type FramingTimelineSegment = {
  startSeconds: number;
  endSeconds: number;
  kind: "observed" | "interpolated";
};

export type FramingTimelineLane = {
  kind: FramingTimelineLaneKind;
  label: string;
  points: FramingTimelinePoint[];
  segments: FramingTimelineSegment[];
  incomplete: boolean;
};

export type FramingTimelineModel = {
  lanes: FramingTimelineLane[];
  durationSeconds: number;
  sceneStartSeconds: number | null;
  sceneEndSeconds: number | null;
  historyTruncated: boolean;
};

const ANOMALY_EVENT_MARKERS = [
  "repair",
  "fallback",
  "failed",
  "mismatch",
  "rejected",
];

function pathLane(
  kind: FramingTimelineLaneKind,
  label: string,
  path: readonly FramingCropPathPoint[],
  durationSeconds: number,
): FramingTimelineLane {
  const sorted = [...path].sort((a, b) => a.timeSeconds - b.timeSeconds);
  const points: FramingTimelinePoint[] = sorted.map((point) => ({
    timeSeconds: point.timeSeconds,
    kind: "observed_keyframe",
    label: `${label} keyframe`,
    anomaly: false,
  }));
  const segments: FramingTimelineSegment[] = [];
  for (let index = 0; index < sorted.length - 1; index += 1) {
    segments.push({
      startSeconds: sorted[index].timeSeconds,
      endSeconds: sorted[index + 1].timeSeconds,
      kind: "interpolated",
    });
  }
  if (sorted.length === 1 && durationSeconds > 0) {
    // One keyframe means a stationary window; observed at that instant only.
    segments.push({
      startSeconds: sorted[0].timeSeconds,
      endSeconds: sorted[0].timeSeconds,
      kind: "observed",
    });
  }
  return { kind, label, points, segments, incomplete: false };
}

export function buildFramingTimeline(
  inspector: FramingSceneInspector,
  options: {
    feedbackSceneRelativeSeconds?: number | null;
    feedbackLabel?: string;
  } = {},
): FramingTimelineModel {
  const sceneStart = firstFinite(
    inspector.coordinateSpaces
      .map((space) => space.timelineStartSeconds)
      .filter((value): value is number => value != null),
  );
  const sceneEnd = firstFinite(
    inspector.coordinateSpaces
      .map((space) => space.timelineEndSeconds)
      .filter((value): value is number => value != null),
  );
  const receipt = inspector.appliedReceipt;
  const decision = inspector.decision;
  const pathDuration = Math.max(
    receipt?.durationSeconds ?? 0,
    ...(decision?.cropPath ?? []).map((point) => point.timeSeconds),
    ...(receipt?.cropPath ?? []).map((point) => point.timeSeconds),
    ...(inspector.observations
      .map((observation) => observation.timestampSeconds)
      .filter((value): value is number => value != null) ?? []),
    0,
  );
  const intervalDuration =
    sceneStart != null && sceneEnd != null && sceneEnd > sceneStart
      ? sceneEnd - sceneStart
      : 0;
  const durationSeconds = Math.max(pathDuration, intervalDuration);

  const lanes: FramingTimelineLane[] = [];
  if (decision?.cropPath?.length) {
    lanes.push(
      pathLane(
        "planned_path",
        "Planned crop",
        decision.cropPath,
        durationSeconds,
      ),
    );
  }
  if (receipt?.cropPath?.length) {
    lanes.push(
      pathLane(
        "applied_path",
        "Applied crop",
        receipt.cropPath,
        durationSeconds,
      ),
    );
  }

  const subjectPoints: FramingTimelinePoint[] = inspector.observations
    .filter((observation) => observation.timestampSeconds != null)
    .map((observation) => ({
      timeSeconds: observation.timestampSeconds!,
      kind: "observed_keyframe" as const,
      label: `${observation.subjectId ?? observation.kind ?? "subject"} observed`,
      anomaly: false,
    }))
    .sort((a, b) => a.timeSeconds - b.timeSeconds);
  if (subjectPoints.length > 0) {
    lanes.push({
      kind: "subject_track",
      label: "Subject observations",
      points: subjectPoints,
      segments: [],
      incomplete: false,
    });
  }

  const qcPoints: FramingTimelinePoint[] = [];
  for (const qc of inspector.qcHistory) {
    const anomaly =
      qc.status === "failed" ||
      (qc.issueCodes?.length ?? 0) > 0 ||
      ["missing", "wrong_subject"].includes(qc.semanticVerdict ?? "");
    qcPoints.push({
      timeSeconds: 0,
      kind: "event",
      label: `QC ${qc.status ?? "checked"}${
        qc.issueCodes?.length ? `: ${qc.issueCodes.join(", ")}` : ""
      }`,
      anomaly,
    });
  }
  for (const event of inspector.history) {
    const marker = ANOMALY_EVENT_MARKERS.find(
      (candidate) =>
        event.eventType.includes(candidate) ||
        (event.status ?? "").includes(candidate),
    );
    if (marker) {
      qcPoints.push({
        timeSeconds: 0,
        kind: "event",
        label: event.eventType.replace("scene.framing.", ""),
        anomaly: true,
      });
    }
  }
  if (qcPoints.length > 0) {
    lanes.push({
      kind: "qc_events",
      label: "QC / repair",
      points: qcPoints,
      segments: [],
      incomplete: inspector.historyTruncated,
    });
  }

  if (options.feedbackSceneRelativeSeconds != null) {
    lanes.push({
      kind: "feedback",
      label: "Feedback",
      points: [
        {
          timeSeconds: options.feedbackSceneRelativeSeconds,
          kind: "feedback",
          label: options.feedbackLabel ?? "Reported feedback",
          anomaly: true,
        },
      ],
      segments: [],
      incomplete: false,
    });
  }

  return {
    lanes,
    durationSeconds,
    sceneStartSeconds: sceneStart,
    sceneEndSeconds: sceneEnd,
    historyTruncated: inspector.historyTruncated,
  };
}

function firstFinite(values: readonly number[]): number | null {
  for (const value of values) {
    if (Number.isFinite(value)) return value;
  }
  return null;
}

export function absoluteToSceneRelative(
  absoluteSeconds: number,
  sceneStartSeconds: number | null,
): number | null {
  if (sceneStartSeconds == null) return null;
  return absoluteSeconds - sceneStartSeconds;
}

export function sceneRelativeToAbsolute(
  relativeSeconds: number,
  sceneStartSeconds: number | null,
): number | null {
  if (sceneStartSeconds == null) return null;
  return sceneStartSeconds + relativeSeconds;
}

/**
 * Clamp only for navigation; the caller keeps displaying the original,
 * possibly invalid value with a correlation warning (CROP-WB-010).
 */
export function clampTimeForNavigation(
  timeSeconds: number,
  durationSeconds: number,
): { clamped: number; outOfRange: boolean } {
  if (!Number.isFinite(timeSeconds) || timeSeconds < 0) {
    return { clamped: 0, outOfRange: true };
  }
  if (durationSeconds > 0 && timeSeconds > durationSeconds) {
    return { clamped: durationSeconds, outOfRange: true };
  }
  return { clamped: timeSeconds, outOfRange: false };
}

function anomalies(model: FramingTimelineModel): FramingTimelinePoint[] {
  return model.lanes
    .flatMap((lane) => lane.points)
    .filter((point) => point.anomaly)
    .sort((a, b) => a.timeSeconds - b.timeSeconds);
}

function keyframes(model: FramingTimelineModel): FramingTimelinePoint[] {
  return model.lanes
    .flatMap((lane) => lane.points)
    .filter((point) => point.kind === "observed_keyframe")
    .sort((a, b) => a.timeSeconds - b.timeSeconds);
}

const NAVIGATION_EPSILON = 1 / 1000;

export function nextTimelinePoint(
  model: FramingTimelineModel,
  timeSeconds: number,
  kind: "anomaly" | "keyframe",
): FramingTimelinePoint | null {
  const points = kind === "anomaly" ? anomalies(model) : keyframes(model);
  return (
    points.find(
      (point) => point.timeSeconds > timeSeconds + NAVIGATION_EPSILON,
    ) ?? null
  );
}

export function previousTimelinePoint(
  model: FramingTimelineModel,
  timeSeconds: number,
  kind: "anomaly" | "keyframe",
): FramingTimelinePoint | null {
  const points = kind === "anomaly" ? anomalies(model) : keyframes(model);
  return (
    [...points]
      .reverse()
      .find((point) => point.timeSeconds < timeSeconds - NAVIGATION_EPSILON) ??
    null
  );
}
